import React, {useState, useEffect} from "react";
import { useProfileParams } from "../../../../../../utils/common";
import { useGetBusinessPerformanceDetailsQuery } from "../../../../../../redux/services/businessPerformaceAPI";
import styles from "./AverageBronzeTile.module.scss";
import KPITile from "../../../../../common/KPITile/KPITile";
import KPIDetailTile from "../../../../../common/KPIDetailTile/KPIDetailTile";
import Benchmark from "../../../../../common/Benchmark/Benchmark";
import { useTranslation } from "react-i18next";
import GraphCard from "../../../../../common/GraphCard/GraphCard";
import InsightAI from "../../../../../common/InsightAI/InsightAI";
import Skeleton from "../../../../../common/Skeleton/Skeleton";
import isEmpty from "lodash/isEmpty";
import { useGetAGCAISummaryQuery } from "../../../../../../redux/services/agcAIAPI";
import { isValidLineChartData } from "../../../../../../utils/common";
import ViewModeSwitcher, {
  ViewMode,
} from "../../../../../common/ViewModeSwitcher/ViewModeSwitcher";
import GenericTable from "../../../../../common/GenericTable/GenericTable";

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

export const AverageBronzeTile: React.FC<SummaryProp> = ({
  summary,
  loading = false,
}) => {
  const benchmark = summary?.benchmark;
  const { t } = useTranslation();

  return (
    <KPITile
      title={t("kpiTitles.averageBronze")}
      dataTestId="average-bronze-tile"
      loading={loading}
    >
      <div className={styles.tile_content}>
        <div className={styles.values_wrapper}>
          <div className={styles.value_wrapper}>
            <div className={styles.value_content}>
              {summary?.kpiValue ?? "--"}
            </div>
            <div className={styles.value_subtitle}>
              {summary?.kpiVarianceValue}% YOY
            </div>
          </div>
        </div>

        <div className={styles.bar_wrapper}>
          {
            <Benchmark
              minValue={benchmark?.min}
              maxValue={benchmark?.max}
              medianValue={benchmark?.median}
              currentBenchmarkValue={benchmark?.kpiValue}
              quartileLine={[benchmark?.lowerQuartile ?? 0, benchmark?.upperQuartile ?? 0]}
              benchmarkDataType={summary?.benchmarkDataType}
              isPercentValue={false}
            />
          }
        </div>
      </div>
    </KPITile>
  );
};

export const AverageBronzeDetail: React.FC<{
  kpiCode?: string;
  onClose?: () => void;
}> = ({ kpiCode = "averageBronze", onClose }) => {
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
    const [viewMode, setViewMode] = useState<ViewMode>("left");

  const { data: kpiDetailsResponse, isLoading } =
    useGetBusinessPerformanceDetailsQuery(
      {
        affiliateCode,
        aboNumber,
        payload: { kpis: [{ code: kpiCode }] } as any,
      },
      { skip: !onClose }
    );

  const { data: agcAISummary, isLoading: isLoadingAiSummary } =
    useGetAGCAISummaryQuery(
      {
        affiliateCode,
        aboNumber,
      },
      {
        skip: Number(affiliateCode) === 0 || aboNumber === 0,
        refetchOnMountOrArgChange: true,
      }
    );

  const detailData = kpiDetailsResponse?.body.kpis[0] || {};
 const monthlyViewCharts = detailData?.detail?.monthlyView?.chart || [];
  
  // Extract line chart data
  const lineChartData = monthlyViewCharts.find(
    (chart: any) => chart.chartType === "lineChart"
  );
  const lineChartDetailData = lineChartData?.chartDetail?.lineChartDetail;
  const hasChartData = isValidLineChartData(lineChartDetailData);

  // Extract table data
  const tableChart = monthlyViewCharts.find(
    (chart: any) => chart.chartType === "table"
  );
  const tableData = tableChart?.chartDetail?.tableDetail;

  useEffect(() => {
      if (!hasChartData) {
        setViewMode("right");
      }
    }, [hasChartData]);
  
    if (isLoading) return <Skeleton height="400px" />;

  const agcData = detailData;
    const aiContent =
    agcAISummary?.body?.summary?.length > 300  ? `${agcAISummary?.body?.summary?.slice(0, 300)}...`
    : agcAISummary?.body?.summary;;
  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>{t("kpiTitles.averageBronze")}</div>
        <div className={styles.desc}>
          {t("kpiDescription.averageBronze")}
        </div>
      </div>
      <div className={styles.chart_view_wrapper}>
              <ViewModeSwitcher
                mode={viewMode}
                onChangeMode={setViewMode}
                disabled={false}
                showSwitch={hasChartData}
              />
            </div>
    </div>
  );

  const leftContent =
  viewMode === "left" ? (
    isEmpty(lineChartDetailData) ? (
      <div className={styles.no_data}>{t("noDataAvailable")}</div>
    ) : (
      <GraphCard
        lineChartData={lineChartDetailData}
        currency={agcData?.kpiCurrency || "USD"}
        chartSummaryData={lineChartData?.chartSummary}
      />
    )
  ) : isEmpty(tableData) ? (
    <div className={styles.no_data}>{t("noDataAvailable")}</div>
  ) : (
    <GenericTable
      tableData={tableData}
      kpiCode={kpiCode}
      highlightColumns={[]}
      highlightRows={[0]}
    />
  );

  const rightContent = isLoadingAiSummary ? (
    <Skeleton height="200px" />
  ) : (
    <InsightAI
      updateDate=""
      content={aiContent}
      title={t("ai_summary")}
      showDropdown={false}
      isDropdownEnabled={true}
      flowerIconSize={24}
      titleSize={14}
    />
  );

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="average-bronze-detail"
    />
  );
};

export default AverageBronzeTile;
